import { useState } from 'react'
import { useAdminFetch } from '../hooks/useAdminAPI'
import {
  PageHeader, AdminButton, StatusBadge,
  DataTable, ViewState, AdminIcon,
} from '../components'
import { formatDateTime, formatDurationBetween, computeAcceptRate } from '../utils/format'
import styles from './ScrapeRuns.module.css'

export function ScrapeRuns() {
  const { data, loading, error, refresh } = useAdminFetch('/admin')
  const [expandedRunId, setExpandedRunId] = useState(null)

  if (loading && !data) return <ViewState variant="loading" />
  if (error && !data) return <ViewState variant="error" message={error} onRetry={refresh} />

  const runs = data?.scrapeRuns ?? []

  return (
    <div className={styles.page}>
      <PageHeader
        title="Scrape runs"
        subtitle="History of all scraping pipeline executions"
        actions={
          <AdminButton
            variant="icon-only"
            icon="refresh"
            label="Refresh data"
            onClick={refresh}
            disabled={loading}
          />
        }
      />

      {runs.length === 0 && data && (
        <ViewState variant="empty" message="No scrape runs found." />
      )}

      {runs.length > 0 && (
        <DataTable>
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Status</th>
              <th>Started</th>
              <th>Mode</th>
              <th className={styles.numberCol}>Extracted</th>
              <th className={styles.numberCol}>Accepted</th>
              <th className={styles.numberCol}>Errors</th>
              <th className={styles.numberCol}>Duration</th>
              <th className={styles.numberCol}>Accept rate</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(run => {
              const acceptRate = computeAcceptRate(run.extracted, run.accepted)
              const isExpanded = expandedRunId === run.id

              return (
                <>
                  <tr
                    key={run.id}
                    className={`${styles.clickableRow} ${run.status === 'failed' ? styles.rowError : run.status === 'partial' ? styles.rowWarning : ''} ${isExpanded ? styles.rowExpanded : ''}`}
                    onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                  >
                    <td className={styles.expandIcon}>
                      <AdminIcon name={isExpanded ? 'chevron-down' : 'chevron-right'} size={14} />
                    </td>
                    <td><StatusBadge status={run.status} /></td>
                    <td className={styles.mono}>{formatDateTime(run.startedAt)}</td>
                    <td className={styles.tertiary}>{run.mode}</td>
                    <td className={styles.number}>{run.extracted}</td>
                    <td className={styles.number}>{run.accepted}</td>
                    <td className={styles.number}>
                      {run.errors > 0
                        ? <span className={styles.errorText}>{run.errors}</span>
                        : '—'
                      }
                    </td>
                    <td className={`${styles.number} ${styles.mono}`}>
                      {formatDurationBetween(run.startedAt, run.completedAt)}
                    </td>
                    <td className={styles.number}>
                      {acceptRate != null ? `${acceptRate}%` : '—'}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${run.id}-detail`} className={styles.detailRow}>
                      <td colSpan={9}>
                        <RunDetail runId={run.id} />
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </DataTable>
      )}
    </div>
  )
}

/** Expanded detail for a single run — shows sources and allows drilling into products */
function RunDetail({ runId }) {
  const { data, loading, error } = useAdminFetch(`/admin/scrape-report?runId=${runId}`)
  const [selectedSource, setSelectedSource] = useState(null)

  if (loading) return <div className={styles.detailLoading}>Loading report...</div>
  if (error) return <div className={styles.detailError}>Failed to load report: {error}</div>
  if (!data?.sources?.length) return <div className={styles.detailEmpty}>No product report data for this run. Reports are captured starting from the next scrape.</div>

  return (
    <div className={styles.detailContainer}>
      <div className={styles.sourceList}>
        <h4 className={styles.detailHeading}>Sources</h4>
        {data.sources.map(source => {
          const isActive = selectedSource === source.source
          const rate = source.total > 0
            ? Math.round((source.accepted / source.total) * 100)
            : 0
          const hasIssue = source.accepted === 0 && source.total > 0

          return (
            <button
              key={source.source}
              className={`${styles.sourceButton} ${isActive ? styles.sourceActive : ''} ${hasIssue ? styles.sourceWarning : ''}`}
              onClick={() => setSelectedSource(isActive ? null : source.source)}
            >
              <span className={styles.sourceName}>{source.source}</span>
              <span className={styles.sourceStats}>
                <span className={styles.sourceAccepted}>{source.accepted}</span>
                <span className={styles.sourceSep}>/</span>
                <span className={styles.sourceTotal}>{source.total}</span>
                <span className={`${styles.sourceRate} ${rate === 0 && source.total > 0 ? styles.rateZero : rate === 100 ? styles.rateFull : ''}`}>
                  {rate}%
                </span>
              </span>
            </button>
          )
        })}
      </div>

      {selectedSource && (
        <SourceProducts runId={runId} source={selectedSource} />
      )}
    </div>
  )
}

/** Product-level detail for a single source within a run */
function SourceProducts({ runId, source }) {
  const { data, loading, error } = useAdminFetch(`/admin/scrape-report?runId=${runId}&source=${encodeURIComponent(source)}`)

  if (loading) return <div className={styles.detailLoading}>Loading products...</div>
  if (error) return <div className={styles.detailError}>Failed: {error}</div>
  if (!data?.products?.length) return <div className={styles.detailEmpty}>No products found.</div>

  const accepted = data.products.filter(p => p.accepted)
  const rejected = data.products.filter(p => !p.accepted)

  return (
    <div className={styles.productSection}>
      <h4 className={styles.detailHeading}>
        {source}
        <span className={styles.productCount}>
          {accepted.length} accepted, {rejected.length} rejected
        </span>
      </h4>

      <DataTable compact>
        <thead>
          <tr>
            <th>Product</th>
            <th style={{ width: 60 }}>Bio</th>
            <th style={{ width: 90 }}>Category</th>
            <th style={{ width: 80 }}>Tier</th>
            <th style={{ width: 80 }}>Status</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {data.products.map((p, i) => (
            <tr key={i} className={p.accepted ? styles.productAccepted : styles.productRejected}>
              <td className={styles.productName}>{p.name}</td>
              <td>
                {p.isBio === true && <span className={styles.bioYes}>bio</span>}
                {p.isBio === false && <span className={styles.bioNo}>no</span>}
                {p.isBio === null && <span className={styles.bioUnknown}>?</span>}
              </td>
              <td className={styles.tertiary}>{p.category ? formatCategory(p.category) : '—'}</td>
              <td>
                {p.tier && <span className={`${styles.tierBadge} ${styles[`tier_${p.tier.replace('-', '_')}`] || ''}`}>{formatTier(p.tier)}</span>}
                {!p.tier && <span className={styles.tertiary}>—</span>}
              </td>
              <td>
                {p.accepted
                  ? <span className={styles.statusAccepted}>accepted</span>
                  : <span className={styles.statusRejected}>rejected</span>
                }
              </td>
              <td className={styles.tertiary}>
                {p.rejectReason ? formatRejectReason(p.rejectReason) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  )
}

function formatTier(tier) {
  const labels = {
    'pre-filter': 'marker check',
    'cache': 'cache',
    'semantic': 'semantic',
    'llm': 'LLM',
    'fallback': 'fallback',
    'override': 'override',
  }
  return labels[tier] ?? tier
}

function formatRejectReason(reason) {
  const labels = {
    not_bio: 'Not organic',
    no_category: 'No produce category',
    no_marker: 'No bio marker',
    filtered: 'Filtered out',
    no_classification: 'Not classified',
  }
  return labels[reason] ?? reason
}

function formatCategory(category) {
  return category.charAt(0).toUpperCase() + category.slice(1)
}
